/**
 * Created by piotr.pozniak@thebeaverhead.com on 20/11/2023.
 *
 * Untitled UI restyle — no design handoff exists for this page (internal
 * superadmin tool, not customer-facing); styled to match the established
 * UsersPage conventions. Name/email filtering, column sort and route-based
 * pagination are preserved from the original. The legacy per-row
 * background-color + "Table colors explanation" legend is replaced by a
 * single Status badge column (see OrganizationsPage/organizationStatus.js).
 * Editing now links to the real /organization/edit/:uuid page
 * (AdminOrgEditModal) instead of the old inline modal, whose reducer cases
 * (activate/update) were dead code — the organizations reducer never
 * actually implemented them.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTemplate from "./../templates/PageTemplate";
import Card from "../theme/untitled/Card/Card";
import Pagination from "../theme/untitled/Pagination/Pagination";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import LoadingIndicator from "../../calendar/components/LoadingIndicator";
import OrganizationsToolbar from "./OrganizationsPage/OrganizationsToolbar";
import OrganizationsTable from "./OrganizationsPage/OrganizationsTable";
import DeleteOrganizationDialog from "./OrganizationsPage/DeleteOrganizationDialog";
import OrganizationDeletedToast from "./OrganizationsPage/OrganizationDeletedToast";
import { useOrganizationsStore } from "../../../hooks/redux/organizations";
import { useAutologinStore } from "../../../hooks/redux/autologin";
import { store } from "../store";
import { UserRoles } from "../consts/crm";
import { useUserStore } from "../../../hooks/redux/user";
import appConfig from "../../../appConfig";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import { useCalendarsStore } from "../../../hooks/redux/calendars";
import { useDomainsStore } from "../../../hooks/redux/domains";
import { useAppTagStore } from "../../../hooks/redux/appTags";
import styles from "./OrganizationsPage/OrganizationsPage.module.scss";

const OrganizationsPageContent = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const showToast = useToast();
  const { t } = useTranslation();

  const { user } = useUserStore();
  const {
    organizations,
    fetch: fetchOrganizations,
    deleteOrganization,
  } = useOrganizationsStore();
  const { autologinUser } = useAutologinStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { appTags, refreshTags } = useAppTagStore();
  const { integrations } = useIntegrationsStore();
  const { calendars } = useCalendarsStore();
  const { domains } = useDomainsStore();

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search]
  );

  const [order, setOrder] = useState({
    field: queryParams.get("sort"),
    direction: queryParams.get("direction"),
  });
  const [nameDraft, setNameDraft] = useState(organizations.filters.name || "");
  const [emailDraft, setEmailDraft] = useState(
    organizations.filters.email || ""
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletedOrgName, setDeletedOrgName] = useState(null);

  // Dispatched thunks here always resolve (never reject) — this is how we
  // read the real post-await error/success state, not a stale render-start
  // closure. Same pattern as UsersPage.
  const organizationsRef = useRef(organizations);
  organizationsRef.current = organizations;

  const refreshOrganizationsList = useCallback(() => {
    const page = match.params.page;

    fetchOrganizations(
      page ? page * 1 : 1,
      50,
      organizations.filters.name,
      organizations.filters.email,
      queryParams.get("sort"),
      queryParams.get("direction")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.page, window.location.search]);

  useEffect(() => {
    refreshOrganizationsList();
  }, [match.params.page, window.location.search]);

  const onPaginationPageChange = useCallback(
    (page) => {
      let url = `/${appConfig.links.organizations}/${page}`;

      if (order.field) {
        url += `?sort=${order.field}`;

        if (order.direction) {
          url += `&direction=${order.direction}`;
        }
      }

      history.push(url);
      window.scrollTo({ top: 0 });
    },
    [order, history]
  );

  useEffect(() => {
    onPaginationPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.field, order.direction]);

  // Keeps the filter inputs in sync with whatever was actually applied
  // (initial URL load, Search, Clear, or a sort/page change that re-fetches
  // with the last-applied filters).
  useEffect(() => {
    setNameDraft(organizations.filters.name || "");
    setEmailDraft(organizations.filters.email || "");
  }, [organizations.filters.name, organizations.filters.email]);

  useEffect(() => {
    if (appTags.refreshTagsSuccess) {
      showToast({ variant: "success", message: "Intercom tags refreshed." });
    }
  }, [appTags.refreshTagsSuccess, showToast]);

  const loading =
    organizations.fetch || organizations.delete || appTags.refreshTags;

  const onAutoLogin = useCallback(
    async (organization) => {
      const autologinAsUser = organization.users.find(
        (orgUser) => orgUser.role === UserRoles.owner
      );

      await autologinUser(
        autologinAsUser.email,
        user,
        {
          model: currentOrganization.model,
          subscription: currentOrganization.subscription,
        },
        {
          integrations: integrations.collection,
          domains: domains.collection,
          calendars: calendars.collection,
        }
      );

      if (store.getState().autologin.loginSuccess) {
        history.push("/dashboard");
      }
    },
    [
      autologinUser,
      user,
      currentOrganization,
      integrations.collection,
      domains.collection,
      calendars.collection,
      history,
    ]
  );

  const onRefreshIntercomTags = useCallback(
    (organization) => refreshTags("company", organization.uuid),
    [refreshTags]
  );

  const onShowDetails = useCallback(
    (organization) =>
      history.push(
        `/${appConfig.links.organization}/edit/${organization.uuid}`
      ),
    [history]
  );

  const onOpenDelete = useCallback(
    (organization) => setDeleteTarget(organization),
    []
  );
  const onCloseDelete = useCallback(() => setDeleteTarget(null), []);

  const onConfirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }

    await deleteOrganization(deleteTarget.uuid);

    const latest = organizationsRef.current;
    if (!latest.deleteError) {
      setDeletedOrgName(deleteTarget.name);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteOrganization]);

  const onCloseDeletedToast = useCallback(() => setDeletedOrgName(null), []);

  const onChangeOrder = useCallback((field, currentDirection) => {
    const directionsMapping = { null: "asc", asc: "desc", desc: null };

    setOrder((prev) => ({
      field,
      direction:
        prev.field === field
          ? directionsMapping[String(currentDirection)]
          : "asc",
    }));
  }, []);

  const onNameDraftChange = useCallback(
    (e) => setNameDraft(e.target.value),
    []
  );
  const onEmailDraftChange = useCallback(
    (e) => setEmailDraft(e.target.value),
    []
  );

  const isFiltered = !!(
    organizations.filters.name || organizations.filters.email
  );

  const onSubmitFilter = useCallback(
    (e) => {
      e.preventDefault();
      fetchOrganizations(1, 50, nameDraft, emailDraft);
    },
    [fetchOrganizations, nameDraft, emailDraft]
  );

  const onClearFilter = useCallback(() => {
    setNameDraft("");
    setEmailDraft("");
    fetchOrganizations(1, 50, "", "");
  }, [fetchOrganizations]);

  const totalCount = organizations.pagination.count;
  const totalPages =
    Math.ceil(totalCount / organizations.pagination.perPage) || 0;

  const orgCountLabel = `${totalCount} ${
    totalCount === 1 ? "organization" : "organizations"
  }`;

  const tableContent = organizations.collection.length ? (
    <OrganizationsTable
      organizations={organizations.collection}
      orderField={order.field}
      orderDirection={order.direction}
      onOrder={onChangeOrder}
      rowsDisabled={loading}
      onDelete={onOpenDelete}
      onAutoLogin={onAutoLogin}
      onShowDetails={onShowDetails}
      onRefreshIntercomTags={onRefreshIntercomTags}
    />
  ) : (
    <div className={styles.empty}>
      <p className={styles.emptyText}>
        {isFiltered
          ? "No organizations match these filters."
          : "No organizations yet."}
      </p>
    </div>
  );

  const paginationEl =
    totalPages > 1 ? (
      <Pagination
        page={organizations.pagination.page}
        totalPages={totalPages}
        onPageChange={onPaginationPageChange}
      />
    ) : null;

  const loadingIndicator = loading ? <LoadingIndicator /> : null;

  const deletedToast = deletedOrgName ? (
    <OrganizationDeletedToast
      name={deletedOrgName}
      onDismiss={onCloseDeletedToast}
    />
  ) : null;

  return (
    <PageTemplate sidebarId="organizations" title={t("Organizations")}>
      <Card>
        <OrganizationsToolbar
          name={nameDraft}
          email={emailDraft}
          onNameChange={onNameDraftChange}
          onEmailChange={onEmailDraftChange}
          onSubmit={onSubmitFilter}
          onClear={onClearFilter}
          disabled={loading}
          isFiltered={isFiltered}
        />

        {tableContent}

        <Card.Footer>
          <span className={styles.footerCount}>{orgCountLabel}</span>
          {paginationEl}
          {loadingIndicator}
        </Card.Footer>
      </Card>

      <DeleteOrganizationDialog
        organization={deleteTarget}
        busy={!!deleteTarget && organizations.delete}
        error={deleteTarget ? organizations.deleteError : null}
        onConfirm={onConfirmDelete}
        onCancel={onCloseDelete}
      />

      {deletedToast}
    </PageTemplate>
  );
};

const OrganizationsPage = () => (
  <ToastProvider>
    <OrganizationsPageContent />
  </ToastProvider>
);

export default OrganizationsPage;
